import React, { useState, useEffect, useCallback } from "react";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { UserGalleryService } from "../services/UserGalleryServices";
import { Image as ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Images() {
    const [galleryItems, setGalleryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Lightbox State
    const [selectedIndex, setSelectedIndex] = useState(null);
    const isOpen = selectedIndex !== null;

    // Touch Swipe Logic
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 50;

    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        try {
            const data = await UserGalleryService.getImages();
            const rotations = ["rotate-1", "-rotate-1", "rotate-2", "-rotate-2", "rotate-3", "-rotate-3"];
            const itemsWithRotation = data.map(item => ({
                ...item,
                rotation: rotations[Math.floor(Math.random() * rotations.length)]
            }));
            setGalleryItems(itemsWithRotation);
        } catch (err) {
            console.error("Failed to fetch gallery images:", err);
            setError("Unable to load the photo gallery at this time.");
        } finally {
            setLoading(false);
        }
    };

    const closeLightbox = useCallback(() => {
        setSelectedIndex(null);
        document.body.style.overflow = 'auto';
    }, []);

    const showNext = useCallback(() => {
        setSelectedIndex(prev => (prev + 1) % galleryItems.length);
    }, [galleryItems.length]);

    const showPrev = useCallback(() => {
        setSelectedIndex(prev => (prev - 1 + galleryItems.length) % galleryItems.length);
    }, [galleryItems.length]);

    const openLightbox = (index) => {
        setSelectedIndex(index);
        document.body.style.overflow = 'hidden';
    };

    // Keyboard Support
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowRight') showNext();
            if (e.key === 'ArrowLeft') showPrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeLightbox, showNext, showPrev]);

    // Touch Handlers
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };
    const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        if (distance > minSwipeDistance) showNext();
        else if (distance < -minSwipeDistance) showPrev();
    };

    const LoadingSkeleton = () => (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 px-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={`transform ${i % 2 === 0 ? 'rotate-1' : '-rotate-1'}`}>
                    <div className="bg-white p-4 pb-12 shadow-lg border border-black/10 animate-pulse">
                        <div className="aspect-[4/3] bg-gray-200 mb-4" />
                        <div className="h-4 w-3/4 bg-gray-200 mx-auto rounded" />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="min-h-screen bg-white">
            <SEO title="Photo Gallery" description="View photo galleries of live performances, studio sessions, and moments behind the scenes." url="/gallery/images" />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                    <div className="mb-16 sm:mb-24 text-center">
                        <h1
                            className="text-4xl sm:text-5xl lg:text-7xl font-normal text-black mb-6"
                            style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                        >
                            Photo Gallery
                        </h1>
                        <div className="w-24 h-[3px] bg-jazz-brown mx-auto mb-8" />
                        <p
                            className="max-w-2xl mx-auto text-black font-medium text-lg leading-relaxed font-serif italic"
                            style={{ fontFamily: "Crimson Pro, Georgia, serif" }}
                        >
                            Moments captured in time, memories made through music.
                        </p>
                    </div>

                    <div className="min-h-[600px]">
                        {loading ? (
                            <LoadingSkeleton />
                        ) : error ? (
                            <div className="text-center py-20 px-6 bg-[#FAFAFA] rounded-3xl border border-dashed border-black/20">
                                <ImageIcon size={48} className="mx-auto text-black/40 mb-4" />
                                <p className="text-black font-serif italic text-lg leading-relaxed">{error}</p>
                            </div>
                        ) : galleryItems.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 px-2 sm:px-4">
                                {galleryItems.map((item, index) => (
                                    <button
                                        key={item.id || index}
                                        onClick={() => openLightbox(index)}
                                        className={`group outline-none text-left transform transition-all duration-500 hover:scale-105 hover:rotate-0 hover:z-10 ${item.rotation}`}
                                    >
                                        <div className="bg-white p-3 sm:p-4 pb-10 sm:pb-12 shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-black/5 h-full flex flex-col">
                                            <div className="aspect-[4/3] overflow-hidden bg-gray-100 mb-4">
                                                <img
                                                    src={item.file_path}
                                                    alt={item.caption}
                                                    className="w-full h-full object-cover filter sepia-[0.2] transition-all duration-500 group-hover:sepia-0"
                                                />
                                            </div>
                                            <p
                                                className="text-center text-black text-base sm:text-lg mt-auto"
                                                style={{ fontFamily: "Gloria Hallelujah, cursive" }}
                                            >
                                                {item.caption}
                                            </p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 border border-dashed border-black/20 rounded-3xl bg-[#FAFAFA]">
                                <ImageIcon size={48} className="mx-auto text-black/40 mb-4" />
                                <p className="text-black font-serif italic text-lg">The gallery is currently empty.</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Lightbox Modal */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm transition-all duration-300 select-none pb-12 sm:pb-0"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onClick={closeLightbox}
                >
                    {/* Close Button */}
                    <button
                        onClick={closeLightbox}
                        className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 text-white/50 hover:text-white transition-colors z-[110]"
                        aria-label="Close lightbox"
                    >
                        <X size={28} className="sm:w-8 sm:h-8" />
                    </button>

                    {/* Navigation Buttons (Now visible on all devices) */}
                    <button
                        onClick={(e) => { e.stopPropagation(); showPrev(); }}
                        className="absolute left-1 sm:left-8 p-1 sm:p-3 text-white/40 hover:text-white hover:bg-white/10 rounded-full transition-all z-[110]"
                        aria-label="Previous image"
                    >
                        <ChevronLeft size={36} className="sm:w-12 sm:h-12" />
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); showNext(); }}
                        className="absolute right-1 sm:right-8 p-1 sm:p-3 text-white/40 hover:text-white hover:bg-white/10 rounded-full transition-all z-[110]"
                        aria-label="Next image"
                    >
                        <ChevronRight size={36} className="sm:w-12 sm:h-12" />
                    </button>

                    {/* Content Container */}
                    <div className="w-full max-w-6xl px-4 sm:px-12 flex flex-col items-center">
                        <div
                            className="relative group bg-white shadow-2xl transition-transform duration-300 p-2 sm:p-5"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={galleryItems[selectedIndex].file_path}
                                alt={galleryItems[selectedIndex].caption}
                                className="max-h-[70vh] sm:max-h-[80vh] w-auto object-contain mx-auto"
                                style={{ pointerEvents: 'none' }}
                            />

                            {/* Counter/Info overlay */}
                            <div className="mt-4 sm:mt-6 text-center">
                                <p
                                    className="text-black text-xl sm:text-3xl mb-1 truncate px-2"
                                    style={{ fontFamily: "Gloria Hallelujah, cursive" }}
                                >
                                    {galleryItems[selectedIndex].caption}
                                </p>
                                <p className="text-black/40 text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase">
                                    {selectedIndex + 1} / {galleryItems.length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <JazzFooter />

            <style jsx>{`
                @import url('https://fonts.googleapis.com/css2?family=Gloria+Hallelujah&display=swap');
            `}</style>
        </div>
    );
}
